<template>
  <!-- O popup no cursor do editor (pedido do usuário): aberto pelo `@` ou pelo botão da barra. Sem
       ativador — a posição é a do cursor (`target`), e quem decide quando abre é o editor -->
  <v-menu :model-value="true" :target="target" location="bottom start" :close-on-content-click="false"
          :open-on-click="false" :persistent="false" @update:model-value="!$event && $emit('close')">
    <v-card class="mention-picker" density="compact">
      <v-list density="compact" class="py-0">
        <!-- Os itens, sempre primeiro, mesmo antes de haver o que buscar: sem isto, quem abre o popup
             vazio não descobre que pode citar um item (relato do usuário). A linha é só dica, e não se
             escolhe; achados, os itens vêm nos grupos de cada coleção, abaixo -->
        <template v-if="!foundItems">
          <v-list-subheader class="mention-picker-group">{{ $tt('mention.items') }}</v-list-subheader>
          <v-list-item disabled>
            <span class="mention-picker-row">
              <v-icon size="small" start>mdi-magnify</v-icon>{{ itemsHint }}
              <span class="mention-picker-detail">{{ $tt('mention.item-syntax') }}</span>
            </span>
          </v-list-item>
        </template>
        <template v-for="group of groups" :key="group.key">
          <v-list-subheader class="mention-picker-group">{{ group.name }}</v-list-subheader>
          <!-- `mousedown.prevent`: o clique não tira o foco do editor, que é onde a escolha entra -->
          <v-list-item v-for="row of group.rows" :key="row.index" :active="row.index === active"
                       @mousedown.prevent @click="pick(row.option)" @mouseenter="active = row.index">
            <!-- Cada linha com a sintaxe do que ela insere ao lado — `[[cidade#5]]`, `[[cidade]]`,
                 `[[@joana]]`, `{{sigla}}` —: é o que o modo Markdown mostra, e se aprende escolhendo -->
            <span class="mention-picker-row">
              <ontos-colored-value v-if="row.option.kind === 'item'" :option="row.colored" form="accent"/>
              <ontos-person-line v-else-if="row.option.kind === 'person'" dense :user="row.person.user"
                                 :name="row.person.name" :photo="row.person.photo"/>
              <template v-else><v-icon size="small" start>{{ row.icon }}</v-icon>{{ row.option.label }}</template>
              <span class="mention-picker-detail">{{ row.detail }}</span>
            </span>
          </v-list-item>
        </template>
      </v-list>
    </v-card>
  </v-menu>
</template>

<script lang="ts">
import {Component, Prop, Vue, Watch, toNative} from 'vue-facing-decorator'
import ColoredValue from '@/components/shared/ColoredValue.vue'
import PersonLine from '@/components/shared/PersonLine.vue'
import Idea from '@/types/Idea'
import {EnhancedWorkpace} from '@/types/Workspace'
import {WorkspacePerson} from '@/types/Person'
import {ColoredOption} from '@/types/ColoredOption'
import {MIN_SEARCH_TEXT, SearchResult} from '@/services/SearchService'
import {titleOfInstance} from '@/utils/ontosUtils'
import {instanceColorOf, personName} from '@/utils/propertyDisplay'

/** O que se escolhe: um item, uma coleção, uma pessoa ou um campo do item (`{{campo}}`). */
export type MentionOption =
    | { kind: 'item', idea: string, instance: number, label: string }
    | { kind: 'collection', idea: string, label: string }
    | { kind: 'person', alias: string, label: string }
    | { kind: 'field', alias: string, label: string }

/** `detail`: a sintaxe do que a linha insere, como o modo Markdown a mostra. */
type Row = { index: number, option: MentionOption, colored?: ColoredOption, person?: WorkspacePerson, icon?: string, detail: string }
type Group = { key: string, name: string, rows: Row[] }

/** Quantos de cada grupo: o popup é um atalho, e não a busca — para achar mais, uma letra a mais. */
const PER_GROUP = 5

/** Os metacampos que o texto mostra (`{{_createdAt}}`), com os nomes das fórmulas. */
const META = ['_state', '_createdAt', '_updatedAt', '_stateChangedAt', '_createdBy', '_updatedBy', '_id'] as const

/** Sem acento e em minúsculas: "sao" acha "São Paulo". */
const folded = (text: string) => text.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()

/**
 * O popup das referências do markdown (ver `markdownMentions`): os itens de todas as coleções, pela
 * busca do ambiente (a do Ctrl K, a partir de `MIN_SEARCH_TEXT` letras); as coleções e as pessoas, pelo
 * nome, no que a tela já tem; e os campos do item, para o `{{campo}}`.
 *
 * O teclado é do editor, que o repassa (`move`, `choose`): o cursor continua no texto enquanto se escolhe.
 */
@Component({
  name: 'ontos-mention-picker', emits: ['pick', 'close'],
  components: {'ontos-colored-value': ColoredValue, 'ontos-person-line': PersonLine}
})
class MentionPicker extends Vue {
  /** O que se digitou depois do `@` — vazio pelo botão da barra. */
  @Prop({type: String, default: ''}) readonly query!: string
  /** Onde o popup aparece: a posição do cursor na tela, `[x, y]`. */
  @Prop({type: Array, required: true}) readonly target!: [number, number]
  @Prop({type: Object, required: true}) readonly workspace!: EnhancedWorkpace
  /** A coleção do item em edição, para os campos do `{{campo}}`. */
  @Prop({type: Object, default: null}) readonly idea!: Idea | null

  result: SearchResult | null = null
  searching = false
  active = 0
  /** Só a última busca vale: elas não voltam necessariamente em ordem. */
  ticket = 0
  timer: ReturnType<typeof setTimeout> | undefined = undefined

  created() {
    this.search()
  }

  beforeUnmount() {
    clearTimeout(this.timer)
  }

  @Watch('query')
  onQuery() {
    this.active = 0
    clearTimeout(this.timer)
    //Uma busca por pausa da digitação, e não por tecla
    this.timer = setTimeout(() => this.search(), 250)
  }

  async search() {
    const ticket = ++this.ticket
    if (this.query.trim().length < MIN_SEARCH_TEXT) {
      this.result = null
      return
    }
    this.searching = true
    try {
      const result = await this.$search.find({systemAlias: this.workspace.alias}, this.query.trim())
      if (ticket === this.ticket) this.result = result
    } catch (err) {
      console.warn('Não foi possível buscar os itens para a referência', err)
    } finally {
      if (ticket === this.ticket) this.searching = false
    }
  }

  get groups(): Group[] {
    const needle = folded(this.query.trim())
    const matches = (...texts: Array<string | undefined>) => !needle || texts.some(text => text && folded(text).includes(needle))
    let index = 0
    const groups: Group[] = []
    const add = (key: string, name: string, rows: Array<Omit<Row, 'index'>>, limit = PER_GROUP) => {
      if (rows.length) groups.push({key, name, rows: rows.slice(0, limit).map(row => ({...row, index: index++}))})
    }
    for (const group of (this.result && this.result.groups) || []) {
      const idea = this.workspace.ideaWith(group.idea)
      if (!idea) continue
      add(`items-${idea.alias}`, idea.pluralName, group.items.map(item => {
        const label = titleOfInstance({_identifier: item.identifier, _instance: item.instance}, idea)
        return {option: {kind: 'item', idea: idea.alias, instance: item.instance, label},
          colored: {id: item.instance, name: label, color: instanceColorOf(item.instance)},
          detail: `[[${idea.alias}#${item.instance}]]`}
      }))
    }
    add('collections', this.$tt('mention.collections') as string, this.workspace.ideas
        .filter(idea => matches(idea.name, idea.pluralName, idea.alias))
        .map(idea => ({option: {kind: 'collection', idea: idea.alias, label: idea.pluralName || idea.name}, icon: 'mdi-table',
          detail: `[[${idea.alias}]]`})))
    //Os campos do item antes das pessoas, e os metacampos num grupo deles, "Sobre este item", com a situação
    //primeiro (pedido do usuário): no mesmo grupo dos campos, o corte de 5 os deixava sempre de fora. Esse
    //grupo vai inteiro: a lista é fixa e curta, e o corte é para as que crescem — com ele, ainda sobravam dois
    const fields = (key: string, name: string, list: Array<{ alias: string, name: string }>, limit?: number) => add(key, name, list
        .filter(field => matches(field.name, field.alias))
        .map(field => ({option: {kind: 'field', alias: field.alias, label: field.name}, icon: 'mdi-code-braces',
          detail: `{{${field.alias}}}`})), limit)
    if (this.idea) {
      fields('fields', this.$tt('mention.fields') as string,
          this.idea.properties.map(property => ({alias: property.alias, name: property.name})))
      fields('meta', this.$tt('mention.about-item') as string, META.filter(alias => alias !== '_state' || this.idea!.workflow)
          .map(alias => ({alias, name: this.$tt(`mention.meta.${alias}`) as string})), META.length)
    }
    add('people', this.$tt('mention.people') as string, this.workspace.people
        .filter(person => person.alias && matches(person.name, person.alias))
        .map(person => ({option: {kind: 'person', alias: person.alias!, label: personName(person)}, person,
          detail: `[[@${person.alias}]]`})))
    return groups
  }

  /** A busca achou itens? Senão, o grupo Itens fica com a dica — ver o template. */
  get foundItems(): boolean {
    return this.groups.some(group => group.key.startsWith('items-'))
  }

  /** A dica do grupo Itens: quantas letras faltam, que está buscando, ou que não achou. */
  get itemsHint(): string {
    if (this.query.trim().length < MIN_SEARCH_TEXT) return this.$tt('mention.items-hint', {count: MIN_SEARCH_TEXT}) as string
    return (this.searching ? this.$tt('mention.searching') : this.$tt('mention.none')) as string
  }

  get rows(): Row[] {
    return this.groups.flatMap(group => group.rows)
  }

  /** As setas do editor: anda pelas linhas, em volta. */
  move(delta: number) {
    const count = this.rows.length
    if (count) this.active = (this.active + delta + count) % count
  }

  /** O Enter do editor: escolhe a linha ativa. Devolve se havia o que escolher. */
  choose(): boolean {
    const row = this.rows[this.active]
    if (row) this.pick(row.option)
    return !!row
  }

  pick(option: MentionOption) {
    this.$emit('pick', option)
  }
}

export default toNative(MentionPicker)
</script>

<style scoped>
.mention-picker {
  min-width: 280px;
  max-width: 420px;
  max-height: 360px;
  overflow-y: auto;
}

.mention-picker-group {
  min-height: 28px;
}

.mention-picker-row {
  display: inline-flex;
  align-items: center;
}

.mention-picker-detail {
  margin-left: 8px;
  opacity: 0.6;
  font-family: monospace;
  font-size: 0.85em;
}

</style>
